import { Link } from "react-router"

import { cn } from "~/lib/utils"

// Partner / client trust row. A static, evenly-spaced grid (Vercel / Linear
// style) so every mark reads clearly — no marquee, no scroll. At rest each
// logo is a muted single tone; on hover it comes to full strength, and the
// full-colour marks reveal their colour. A logo with `href` is a link.
//
// Files live in public/logos (Google: gilbarbara/logos; BT + Telia: Wikimedia
// Commons; Sites USA / Spatial / GeoStrategies / RetailNext: company assets).
// `width`/`height` are the files' intrinsic dimensions so the browser reserves
// aspect ratio before the lazy image lands.

type Partner = {
  name: string
  src: string
  width: number
  height: number
  // Full-colour logo: greyscale at rest, colour on hover. Others are flat ink.
  color?: boolean
  // Make the logo a link.
  href?: string
  // Row layout only: the logo's height in px, set by eye so every mark
  // carries the same visual weight (a round BT needs more height than a
  // long RetailNext wordmark; the heavy Sites USA block needs less).
  rowH: number
}

const PARTNERS: Partner[] = [
  {
    name: "RetailNext",
    rowH: 20,
    src: "/logos/retailnext.png",
    width: 892,
    height: 147,
    href: "/blog/retailnext-partnership",
  },
  {
    name: "Google",
    rowH: 26,
    src: "/logos/google.svg",
    width: 512,
    height: 168,
    color: true,
  },
  {
    name: "BT",
    rowH: 34,
    src: "/logos/bt.svg",
    width: 999,
    height: 999,
    color: true,
  },
  {
    name: "Telia",
    rowH: 21,
    src: "/logos/telia.svg",
    width: 1639,
    height: 283,
    color: true,
  },
  {
    name: "The Retail Coach",
    rowH: 21,
    src: "/logos/retail-coach.webp",
    width: 800,
    height: 140,
  },
  {
    name: "Sites USA",
    rowH: 18,
    src: "/logos/sitesusa.svg",
    width: 740,
    height: 160,
  },
  {
    name: "Spatial",
    rowH: 17,
    src: "/logos/spatial.svg",
    width: 250,
    height: 44,
  },
  {
    name: "GeoStrategies",
    rowH: 22,
    src: "/logos/geostrategies.svg",
    width: 264,
    height: 55,
    color: true,
  },
]

function Logo({ p }: { p: Partner }) {
  const img = (
    <img
      src={p.src}
      alt={p.name}
      width={p.width}
      height={p.height}
      loading="lazy"
      decoding="async"
      className={cn(
        "max-h-full w-auto max-w-[108px] object-contain opacity-70 transition duration-200 group-hover:opacity-100 sm:max-w-[150px]",
        p.color
          ? "grayscale group-hover:grayscale-0"
          : "[filter:brightness(0)]",
      )}
    />
  )
  const cell = "group flex h-7 items-center justify-center sm:h-9"

  return p.href ? (
    <Link
      to={p.href}
      className={cn(cell, "rounded")}
      aria-label={`${p.name}, learn more`}
    >
      {img}
    </Link>
  ) : (
    <div className={cell}>{img}</div>
  )
}

// One logo in the single-row layout: its own optical height, a little
// smaller on phones.
function RowLogo({ p }: { p: Partner }) {
  const img = (
    <img
      src={p.src}
      alt={p.name}
      width={p.width}
      height={p.height}
      loading="lazy"
      decoding="async"
      style={{ "--h": `${p.rowH}px` } as React.CSSProperties}
      className={cn(
        "h-[calc(var(--h)*0.8)] w-auto max-w-full object-contain opacity-70 transition duration-200 group-hover:opacity-100 sm:h-[var(--h)]",
        p.color
          ? "grayscale group-hover:grayscale-0"
          : "[filter:brightness(0)]",
      )}
    />
  )
  const cell = "group flex h-9 min-w-0 max-w-full items-center"
  return p.href ? (
    <Link
      to={p.href}
      className={cn(cell, "rounded")}
      aria-label={`${p.name}, learn more`}
    >
      {img}
    </Link>
  ) : (
    <div className={cell}>{img}</div>
  )
}

// The logos as one evenly weighted row (the alternative page, Anna
// 2026-11-04: "A"). Always even rows, never one widow (Anna 2026-11-04: "we
// can't leave ONE widow in the second row"): a fixed grid of 2 columns on
// phones (4 rows), 4 columns from sm (2 rows of 4), and one row of 8 from xl,
// where all eight fit with room. Exported bare, so a section can carry it
// next to other proof.
export function LogoRow({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "mx-auto grid max-w-6xl grid-cols-2 items-center justify-items-center gap-x-6 gap-y-6 sm:grid-cols-4 sm:gap-x-10 xl:flex xl:justify-between xl:gap-x-6",
        className,
      )}
    >
      {PARTNERS.map((p) => (
        <RowLogo key={p.name} p={p} />
      ))}
    </div>
  )
}

// `label={false}` drops the eyebrow line (the alternative page runs without
// eyebrows); the logos then sit straight under the section above.
// `className` overrides the band's ground, e.g. to set it apart from a cream
// hero above it.
// `layout="row"` swaps the two-row grid for LogoRow; the homepage keeps the
// grid.
export function TrustedBy({
  label = true,
  className,
  layout = "grid",
}: {
  label?: boolean
  className?: string
  layout?: "grid" | "row"
}) {
  return (
    <section className={cn("bg-cream py-12 sm:py-14", className)}>
      <div className="u-container text-center">
        {label ? (
          <p className="mb-8 font-flex text-[14px] font-semibold uppercase tracking-[0.08em] text-mute">
            Building what&rsquo;s next together
          </p>
        ) : null}

        {layout === "row" ? (
          <LogoRow />
        ) : (
          <div className="mx-auto grid max-w-4xl grid-cols-2 gap-x-6 gap-y-7 sm:grid-cols-4 sm:gap-x-8 sm:gap-y-8">
            {PARTNERS.map((p) => (
              <Logo key={p.name} p={p} />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
